import { appsClientHeaders } from "./appsClient.js";
import { API_BASE_URL } from "./config.js";
import { setCachedAiBetaAllowed } from "./composeAiGate.js";
import { buildAuthHeader, buildCanonicalString, importPrivateKey, signCanonical, } from "./crypto.js";
async function signedFetch(credentials, method, path, body, extraHeaders) {
    const privateKey = await importPrivateKey(credentials.privateKeyJwk);
    const timestamp = String(Math.floor(Date.now() / 1000));
    const bodyText = body ?? "";
    // FastAPI verifies against the percent-decoded path (request.url.path).
    // Fetch still uses the encoded path on the wire.
    const pathForSignature = signaturePath(path);
    const canonical = await buildCanonicalString(method, pathForSignature, bodyText, timestamp);
    const signature = await signCanonical(privateKey, canonical);
    const auth = buildAuthHeader(credentials.deviceId, timestamp, signature);
    return fetch(`${API_BASE_URL}${path}`, {
        method,
        headers: {
            Authorization: auth,
            ...appsClientHeaders(),
            ...(body !== undefined
                ? { "Content-Type": "application/json" }
                : {}),
            ...extraHeaders,
        },
        body: body !== undefined ? body : undefined,
    });
}
/** Match Starlette/FastAPI request.url.path (percent-decoded, no query). */
function signaturePath(path) {
    const pathname = path.split("?", 1)[0] ?? path;
    try {
        return decodeURIComponent(pathname);
    }
    catch {
        return pathname;
    }
}
async function requireCredentials(storage) {
    const credentials = await storage.getCredentials();
    if (!credentials) {
        throw new Error("Device is not paired");
    }
    return credentials;
}
async function parseSettingsResponse(response) {
    if (response.status === 403) {
        throw new Error("Device is not active. Please pair again.");
    }
    if (!response.ok) {
        const detail = await response.text();
        throw new Error(detail || `Request failed (${response.status})`);
    }
    const data = (await response.json());
    return data.settings;
}
export async function getSettingsForAccount(storage, account) {
    const credentials = await storage.getCredentials(account);
    if (!credentials) {
        throw new Error(`Device is not paired for account: ${account}`);
    }
    const response = await signedFetch(credentials, "GET", "/api/v1/spamassassin/settings");
    return parseSettingsResponse(response);
}
export async function getSettings(storage) {
    const active = await storage.getActiveAccount();
    if (!active) {
        throw new Error("Device is not paired");
    }
    return getSettingsForAccount(storage, active);
}
export async function updateSettings(storage, settings) {
    const credentials = await requireCredentials(storage);
    const body = JSON.stringify({ settings });
    const response = await signedFetch(credentials, "POST", "/api/v1/spamassassin/settings-update", body);
    return parseSettingsResponse(response);
}
export async function updateList(storage, action, email, operation = "add") {
    const credentials = await requireCredentials(storage);
    const body = JSON.stringify({ action, email, operation });
    const response = await signedFetch(credentials, "POST", "/api/v1/spamassassin/list-update", body);
    return parseSettingsResponse(response);
}
export async function updateCustomScore(storage, rule, score, operation = "add") {
    const credentials = await requireCredentials(storage);
    const body = JSON.stringify({ rule, score, operation });
    const response = await signedFetch(credentials, "POST", "/api/v1/spamassassin/score-update", body);
    return parseSettingsResponse(response);
}
export async function getPairedAccount(storage) {
    return storage.getActiveAccount();
}
export async function listPairedAccounts(storage) {
    return storage.listAccounts();
}
export async function unpair(storage, account) {
    const removeLocal = async (target) => {
        await bestEffortServerUnpair(storage, target);
        await storage.removeAccount(target);
    };
    if (account) {
        await removeLocal(account);
        return;
    }
    const active = await storage.getActiveAccount();
    if (active) {
        await removeLocal(active);
        return;
    }
    const accounts = await storage.listAccounts();
    for (const paired of accounts) {
        await bestEffortServerUnpair(storage, paired);
    }
    await storage.clearAllCredentials();
}
async function bestEffortServerUnpair(storage, account) {
    const credentials = await storage.getCredentials(account);
    if (!credentials) {
        return;
    }
    try {
        await signedFetch(credentials, "POST", "/api/v1/unpair", "");
    }
    catch {
        // Offline or server error — still remove local credentials.
    }
}
async function parseApiError(response) {
    if (response.status === 403) {
        throw new Error("Device is not active. Please pair again.");
    }
    const text = await response.text();
    try {
        const data = JSON.parse(text);
        if (typeof data.detail === "string" && data.detail) {
            throw new Error(data.detail);
        }
        if (Array.isArray(data.detail) && data.detail[0]?.msg) {
            throw new Error(data.detail[0].msg);
        }
    }
    catch (error) {
        if (error instanceof Error && error.message !== text) {
            throw error;
        }
    }
    throw new Error(text || `Request failed (${response.status})`);
}
async function parseSieveListResponse(response) {
    if (!response.ok) {
        await parseApiError(response);
    }
    return (await response.json());
}
async function parseSieveScriptResponse(response) {
    if (!response.ok) {
        await parseApiError(response);
    }
    return (await response.json());
}
export async function getSieveCapabilities(storage) {
    const credentials = await requireCredentials(storage);
    const response = await signedFetch(credentials, "GET", "/api/v1/sieve/capabilities");
    if (!response.ok) {
        await parseApiError(response);
    }
    return (await response.json());
}
export async function listSieveScripts(storage) {
    const credentials = await requireCredentials(storage);
    const response = await signedFetch(credentials, "GET", "/api/v1/sieve/scripts");
    return parseSieveListResponse(response);
}
export async function getSieveScript(storage, name) {
    const credentials = await requireCredentials(storage);
    const response = await signedFetch(credentials, "GET", `/api/v1/sieve/script?name=${encodeURIComponent(name)}`);
    return parseSieveScriptResponse(response);
}
export async function createSieveScript(storage, name, content) {
    const credentials = await requireCredentials(storage);
    const body = JSON.stringify({ name, content });
    const response = await signedFetch(credentials, "POST", "/api/v1/sieve/scripts", body);
    return parseSieveScriptResponse(response);
}
export async function updateSieveScript(storage, name, content) {
    const credentials = await requireCredentials(storage);
    const body = JSON.stringify({ content });
    const response = await signedFetch(credentials, "PUT", `/api/v1/sieve/script?name=${encodeURIComponent(name)}`, body);
    return parseSieveScriptResponse(response);
}
export async function deleteSieveScript(storage, name) {
    const credentials = await requireCredentials(storage);
    const response = await signedFetch(credentials, "DELETE", `/api/v1/sieve/script?name=${encodeURIComponent(name)}`);
    if (!response.ok) {
        await parseApiError(response);
    }
}
export async function activateSieveScript(storage, name) {
    const credentials = await requireCredentials(storage);
    const response = await signedFetch(credentials, "POST", `/api/v1/sieve/script/activate?name=${encodeURIComponent(name)}`, "");
    if (!response.ok) {
        await parseApiError(response);
    }
    return (await response.json());
}
export async function createUnsubscribeJobs(storage, messages) {
    const credentials = await requireCredentials(storage);
    const body = JSON.stringify({
        messages: messages.map((m) => ({
            client_message_id: m.clientMessageId,
            sender_email: m.senderEmail,
            reply_to_email: m.replyToEmail,
            subject: m.subject,
            message_id: m.messageId,
            list_unsubscribe: m.listUnsubscribe,
            list_unsubscribe_post: m.listUnsubscribePost,
            html_body: m.htmlBody,
            text_body: m.textBody,
            recipient_email: m.recipientEmail || credentials.imapAccount,
        })),
    });
    const response = await signedFetch(credentials, "POST", "/api/v1/unsubscribe/jobs", body);
    if (!response.ok) {
        await parseApiError(response);
    }
    return (await response.json());
}
export async function listUnsubscribeJobs(storage, limit = 20) {
    const credentials = await requireCredentials(storage);
    const response = await signedFetch(credentials, "GET", `/api/v1/unsubscribe/jobs?limit=${encodeURIComponent(String(limit))}`);
    if (!response.ok) {
        await parseApiError(response);
    }
    return (await response.json());
}
export async function getUnsubscribeJob(storage, jobId) {
    const credentials = await requireCredentials(storage);
    const response = await signedFetch(credentials, "GET", `/api/v1/unsubscribe/jobs/${jobId}`);
    if (!response.ok) {
        await parseApiError(response);
    }
    return (await response.json());
}
export async function completeUnsubscribeMailto(storage, jobId, success, error) {
    const credentials = await requireCredentials(storage);
    const body = JSON.stringify({ success, error: error ?? null });
    const response = await signedFetch(credentials, "PATCH", `/api/v1/unsubscribe/jobs/${jobId}/mailto`, body);
    if (!response.ok) {
        await parseApiError(response);
    }
    return (await response.json());
}
export async function rewriteComposeText(storage, request) {
    const credentials = await requireCredentials(storage);
    const body = JSON.stringify({
        text: request.text,
        instruction: request.instruction,
        count: request.count ?? 1,
        recipient: request.recipient,
        instructions: request.instructions,
        use_web_search: request.useWebSearch ?? false,
        model: request.model,
    });
    const response = await signedFetch(credentials, "POST", "/api/v1/ai/rewrite", body);
    if (!response.ok) {
        await parseApiError(response);
    }
    return (await response.json());
}
export async function suggestReplyTexts(storage, request) {
    const credentials = await requireCredentials(storage);
    const body = JSON.stringify({
        original_subject: request.originalSubject,
        original_from: request.originalFrom,
        original_body: request.originalBody,
        draft_body: request.draftBody,
        count: request.count ?? 3,
        instructions: request.instructions,
        use_web_search: request.useWebSearch ?? false,
        model: request.model,
    });
    const response = await signedFetch(credentials, "POST", "/api/v1/ai/reply-suggestions", body);
    if (!response.ok) {
        await parseApiError(response);
    }
    return (await response.json());
}
export async function getAiModels(storage) {
    const credentials = await requireCredentials(storage);
    const response = await signedFetch(credentials, "GET", "/api/v1/ai/models");
    if (!response.ok) {
        await parseApiError(response);
    }
    const data = (await response.json());
    return {
        provider: data.provider || "ollama",
        defaultModel: data.default_model || "ollama",
        models: Array.isArray(data.models) ? data.models : [],
    };
}
export async function getAiBetaAccess(storage) {
    const credentials = await requireCredentials(storage);
    const response = await signedFetch(credentials, "GET", "/api/v1/ai/beta-access");
    if (!response.ok) {
        await parseApiError(response);
    }
    const data = (await response.json());
    const allowed = data.allowed === true;
    const imapAccount = data.imap_account || credentials.imapAccount;
    setCachedAiBetaAllowed(allowed, imapAccount);
    return {
        allowed,
        imapAccount,
        openDisagreementCount: data.open_disagreement_count ?? 0,
    };
}
export async function checkMessageScam(storage, message, options) {
    const credentials = await requireCredentials(storage);
    const body = JSON.stringify({
        client_message_id: message.clientMessageId,
        message_id: message.messageId,
        subject: message.subject,
        from_header: message.fromHeader,
        sender_email: message.senderEmail,
        reply_to_email: message.replyToEmail,
        recipient_email: message.recipientEmail || credentials.imapAccount,
        text_body: message.textBody,
        html_body: message.htmlBody,
        authentication_results: message.authenticationResults,
        received_spf: message.receivedSpf,
        return_path: message.returnPath,
        dkim_present: message.dkimPresent,
        force: options?.force ?? false,
    });
    const response = await signedFetch(credentials, "POST", "/api/v1/ai/scam-check", body);
    if (!response.ok) {
        await parseApiError(response);
    }
    const data = (await response.json());
    return {
        risk: data.risk,
        summary: data.summary,
        reasons: data.reasons ?? [],
        confidence: data.confidence,
        suggestedActions: data.suggested_actions ?? [],
        heuristicFindings: (data.heuristic_findings ?? []).map((item) => ({
            code: item.code,
            detail: item.detail,
        })),
        jobId: data.job_id ?? undefined,
        cached: data.cached ?? false,
        openDisagreementCount: data.open_disagreement_count ?? 0,
    };
}
export async function submitScamCheckFeedback(storage, message, options) {
    const credentials = await requireCredentials(storage);
    const body = JSON.stringify({
        job_id: options.jobId,
        user_label: options.userLabel,
        client_message_id: message.clientMessageId,
        message_id: message.messageId,
        subject: message.subject,
        from_header: message.fromHeader,
        sender_email: message.senderEmail,
        reply_to_email: message.replyToEmail,
        recipient_email: message.recipientEmail || credentials.imapAccount,
        text_body: message.textBody,
        html_body: message.htmlBody,
        authentication_results: message.authenticationResults,
        received_spf: message.receivedSpf,
        return_path: message.returnPath,
        dkim_present: message.dkimPresent,
    });
    const response = await signedFetch(credentials, "POST", "/api/v1/ai/scam-check/feedback", body);
    if (!response.ok) {
        await parseApiError(response);
    }
    const data = (await response.json());
    return {
        ok: data.ok !== false,
        feedbackId: data.feedback_id,
        userLabel: data.user_label,
        openDisagreementCount: data.open_disagreement_count ?? 0,
    };
}
/** Build a scam verdict from a stored activity job, if present. */
export function scamResultFromJob(job) {
    if (job.job_type !== "email_scam_check") {
        return null;
    }
    const result = job.result;
    if (!result?.risk) {
        return null;
    }
    return {
        risk: result.risk,
        summary: result.summary || "",
        reasons: result.reasons ?? [],
        confidence: result.confidence || "low",
        suggestedActions: result.suggested_actions ?? [],
        heuristicFindings: (result.heuristic_findings ?? []).map((item) => ({
            code: item.code,
            detail: item.detail,
        })),
        jobId: job.id,
        cached: true,
    };
}
async function kerioHeaders(credentials) {
    if (credentials.mailServerType !== "kerio") {
        throw new Error("This account is not paired to a Kerio server");
    }
    if (!credentials.mailboxPassword) {
        throw new Error("Kerio mailbox password is missing. Unpair and pair again from the portal.");
    }
    return { "X-Mailbox-Password": credentials.mailboxPassword };
}
export async function getMailServerType(storage) {
    const credentials = await requireCredentials(storage);
    return credentials.mailServerType === "kerio" ? "kerio" : "dovecot";
}
/** Mailbox quota roots for the active paired account (Dovecot and/or Kerio). */
export async function getQuota(storage) {
    const credentials = await requireCredentials(storage);
    const headers = credentials.mailServerType === "kerio"
        ? await kerioHeaders(credentials)
        : undefined;
    const response = await signedFetch(credentials, "GET", "/api/v1/quota", undefined, headers);
    if (!response.ok) {
        await parseApiError(response);
    }
    return (await response.json());
}
export async function getMailServerHost(storage) {
    const credentials = await requireCredentials(storage);
    const host = credentials.mailServerHost?.trim();
    return host || null;
}
/** Prefer a full email for Kerio DAV path segments and auth username. */
export async function getKerioMailboxEmail(storage) {
    const credentials = await requireCredentials(storage);
    const candidates = [
        credentials.imapAccount,
        ...(credentials.mailboxAliases ?? []),
    ];
    for (const candidate of candidates) {
        const value = candidate?.trim().toLowerCase();
        if (value?.includes("@")) {
            return value;
        }
    }
    return null;
}
/** Update the stored Kerio primary account password (not an app password). */
export async function updateKerioMailboxPassword(storage, password) {
    const credentials = await requireCredentials(storage);
    if (credentials.mailServerType !== "kerio") {
        throw new Error("This account is not paired to a Kerio server");
    }
    const trimmed = password.trim();
    if (!trimmed) {
        throw new Error("Password is required");
    }
    await storage.setCredentials({
        ...credentials,
        mailboxPassword: trimmed,
    });
}
export async function getKerioFilters(storage) {
    const credentials = await requireCredentials(storage);
    const headers = await kerioHeaders(credentials);
    const response = await signedFetch(credentials, "GET", "/api/v1/kerio/filters", undefined, headers);
    if (!response.ok) {
        await parseApiError(response);
    }
    return (await response.json());
}
export async function setKerioFilters(storage, dataStamp, filters) {
    const credentials = await requireCredentials(storage);
    const headers = await kerioHeaders(credentials);
    const body = JSON.stringify({ dataStamp, filters });
    const response = await signedFetch(credentials, "PUT", "/api/v1/kerio/filters", body, headers);
    if (!response.ok) {
        await parseApiError(response);
    }
    return (await response.json());
}
export async function getKerioSpamSettings(storage) {
    const credentials = await requireCredentials(storage);
    const headers = await kerioHeaders(credentials);
    const response = await signedFetch(credentials, "GET", "/api/v1/kerio/spam", undefined, headers);
    if (!response.ok) {
        await parseApiError(response);
    }
    const data = (await response.json());
    return data.settings;
}
export async function setKerioSpamSettings(storage, settings) {
    const credentials = await requireCredentials(storage);
    const headers = await kerioHeaders(credentials);
    const body = JSON.stringify({ settings });
    const response = await signedFetch(credentials, "PUT", "/api/v1/kerio/spam", body, headers);
    if (!response.ok) {
        await parseApiError(response);
    }
    const data = (await response.json());
    return data.settings;
}
export async function getKerioOutOfOffice(storage) {
    const credentials = await requireCredentials(storage);
    const headers = await kerioHeaders(credentials);
    const response = await signedFetch(credentials, "GET", "/api/v1/kerio/out-of-office", undefined, headers);
    if (!response.ok) {
        await parseApiError(response);
    }
    const data = (await response.json());
    return data.settings;
}
export async function setKerioOutOfOffice(storage, settings) {
    const credentials = await requireCredentials(storage);
    const headers = await kerioHeaders(credentials);
    const body = JSON.stringify({ settings });
    const response = await signedFetch(credentials, "PUT", "/api/v1/kerio/out-of-office", body, headers);
    if (!response.ok) {
        await parseApiError(response);
    }
    const data = (await response.json());
    return data.settings;
}
